import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ASSETS_LIB_TABS, EDITOR_VIEW_NAME } from "../core/constants";
import { translate } from "../core/i18n";
import { getDomainCfg } from "../core/state";
import { getViewData, setComponentData, setViewData } from "../core/views";
import { loadLibAssets } from "../features/requests";
import { showLoginRegisterModal } from "./modals/LoginRegisterModal";
import { isShareButtonVisible, showShareModal } from "./modals/ShareModal";
import { DropdownButton, Dropdown, Navbar, Offcanvas } from "react-bootstrap";
import { logoutUser } from "../features/cloudstorage/user";
import { store } from "../core/state";
import { CLOUD_USER } from "../features/cloudstorage/cloud";
import { exportProjectAsZip, isExportAvailable } from "../features/export";
import { getCustomMenuItem } from "../features/custommenuitem";
import { CLICK_EVENT, requestNextStep } from "./StepPopper";
import { isTemplateProject } from "../features/templates";
import { requestEditData } from "./EditProjectData";
import { getCurrentTheme } from "./ThemeContext";
import { CreditIndicator } from "./chat/CreditIndicator";
import { showQuestionModal, hideModal } from "./modals/QuestionModal";
import { deleteProject } from "../core/code/saver";
import { getRedirectUrl, redirectTo } from "../features/redirect";
import { requestExitEditor } from "./EditorNavMenu";
import { useState, useEffect } from "react";
import { downloadProjectAsZip } from "../features/filesystem/fileOperations";
import { getAppCfg } from "../core/app-cfg";

export const MainMenu = (props : { withProject : boolean, menuitems : any[], burger? : boolean, alwaysShow? : boolean }) => {
  const [user] = store.useState(CLOUD_USER);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  
  // Close offcanvas when editDataModal opens
  const [editorState] = store.useState(EDITOR_VIEW_NAME);
  useEffect(() => {
    if (editorState?.data?.editDataVisible) {
      setShowOffcanvas(false);
    }
  }, [editorState?.data?.editDataVisible]);

  const hasProject = props.withProject;

  const menuClicked = () => {
    setShowOffcanvas(true);
    requestNextStep(CLICK_EVENT, "burger-menu")
  }
  
  const handleOffcanvasClose = () => {
    setShowOffcanvas(false);
  }

  const requestAssetsLib = async () => {
    const editorState = getViewData(EDITOR_VIEW_NAME);
    editorState.data = {
      ...editorState.data,
      assetsVisible : true
    }
    const assetsLib = await loadLibAssets(getDomainCfg().product.assetsLibPath);
    setComponentData(ASSETS_LIB_TABS, assetsLib);
    setViewData(EDITOR_VIEW_NAME, editorState);
    requestNextStep(CLICK_EVENT, "asset-lib")
  }

  const requestShareModal = () => {
    showShareModal();
  }

  const downloadProject = async () => {
    const state = getViewData(EDITOR_VIEW_NAME);
    if (state.project?.version === "2.0" && state.data?.projectContent?.tabs) {
      const projectId = state.project?.id || getAppCfg().projectId || 'project';
      const tabs = state.data.projectContent.tabs;
      const v2Changes = (state.data.v2EditorChanges || {});
      const fileContents = new Map();
      for (const tab of tabs) {
        const content = (tab.path in v2Changes && v2Changes[tab.path] !== undefined)
          ? v2Changes[tab.path] : tab.code;
        fileContents.set(tab.path, content);
      }
      await downloadProjectAsZip(projectId, tabs, fileContents);
    } else {
      exportProjectAsZip();
    }
  }

  const triggerLoginModal = () => {
    showLoginRegisterModal(true);
  }

  const triggerRegisterModal = () => {
    showLoginRegisterModal(false);
  }

  const triggerLogout = () => {
    logoutUser();
  }

  const requestDeleteProject = () => {
    const opts = {
      okTitle : translate("delete-project-modal-ok"),
      okColor: "btn-danger",
      cancelTitle : translate("modal-cancel"),
      cancelColor: "btn-secondary"
    }
    showQuestionModal(
      translate("delete-project-modal-title"),
      translate("delete-project-modal-message"),
      deleteProjectAccepted,
      deleteProjectDeclined,
      opts
    );
  }

  const deleteProjectDeclined = () => hideModal();

  const deleteProjectAccepted = async () => {
    hideModal();
    await deleteProject();
    redirectTo(getRedirectUrl(getDomainCfg().viewSettings.projectsView), 3000);
  }


  const features = getDomainCfg().viewSettings.features;
  
  const variant = getCurrentTheme();

  const name = user?.name || user?.userName || 'Coder';
  const loggedIn = user?.loggedIn;

  let greeting = (<><FontAwesomeIcon icon="user" /> { translate('greeting-title', name) } </>);
  if (loggedIn) {
    greeting = <a href={getDomainCfg().product.userProfileLink} target="_blank" rel="noreferrer">{ greeting }</a>
  }
  const styling = "dropdown-item btn btn-outline-secondary";

  const userGreeting = <Dropdown.ItemText key="greeting">{greeting}</Dropdown.ItemText>;
  const dataEditButton = <button className={styling} id='btn-data-edit' title={translate('edit-data-title')} onClick={requestEditData} key='data'><FontAwesomeIcon icon="tags" /> {translate('edit-data-menu')}</button>;
  const deleteProjectButton = <button className={styling} id='btn-delete-project' title={translate('delete-project-title')} onClick={requestDeleteProject} key='delete-project'><FontAwesomeIcon icon="trash" /> {translate('delete-project-menu')}</button>;
  const assetsButton = <button className={styling} id='btn-open-lib' title={translate('open-lib-title')} onClick={requestAssetsLib} key='assets'><FontAwesomeIcon icon="lightbulb" /> {translate('open-lib-menu')}</button>;
  const shareButton = <button className={styling} title={translate('share-title')} onClick={requestShareModal} key='share'><FontAwesomeIcon icon="share-alt" /> {translate('share-menu')}</button>;
  const exportButton = <button className={styling} title={translate('download-title')} onClick={downloadProject} key='export'><FontAwesomeIcon icon="file-export" /> {translate('download-menu')}</button>;
  const signupButton = <button className={styling} title={translate('view-editor-save-title')} onClick={triggerRegisterModal} key='signup'><FontAwesomeIcon icon="save" /> {translate('view-editor-save-title')}</button>;
  const loginButton = <button className={styling} title="Login" onClick={triggerLoginModal} key='login'><FontAwesomeIcon icon="arrow-right" /> Login</button>;
  const logoutButton = <button className={styling} title="Logout" onClick={triggerLogout} key='logout'><FontAwesomeIcon icon="arrow-left" /> Logout</button>;
  const exitButton = <button className={styling} title={translate('exit-btn-title')} onClick={requestExitEditor} key='exit'><FontAwesomeIcon icon="right-to-bracket" /> {translate('exit')}</button>;

  const creditsButton = <CreditIndicator className={styling} />

  const items = props.menuitems?.map((item, index) => {
    if (item.type === "component") {
      if (item.name === "profile") return userGreeting;
      else if (item.name === "data" && hasProject && isTemplateProject()) return dataEditButton;
      else if (item.name === "delete-project" && hasProject) return deleteProjectButton;
      else if (item.name === "assets" && hasProject) return assetsButton;
      else if (item.name === "credits") return creditsButton;
      else if (item.name === "share" && hasProject && isShareButtonVisible()) return shareButton;
      else if (item.name === "export" && hasProject && isExportAvailable()) return exportButton;
      else if (item.name === "assets" && hasProject) return assetsButton;
      else if (item.name === "signup" && features.cloudStorageAvailable && !loggedIn) return signupButton;
      else if (item.name === "login" && features.cloudStorageAvailable && !loggedIn) return loginButton;
      else if (item.name === "logout" && features.cloudStorageAvailable && loggedIn) return logoutButton;
      else if (item.name === "exit" && hasProject) return exitButton;     
    } else if (item.type === "link" || item.type === "button") {      
      return getCustomMenuItem(item, index, item.type !== 'button' ? styling : undefined);
    } else if (item.type === "separator") {
      return <div className="dropdown-divider"/>;
    } else if (item.type === "space") {
      return <div className="space" style={{height: "30px"}}/>;
    }
    return null;
  })

  const dropdownButton = <DropdownButton data-bs-theme={variant} drop="start" align="start" id="dropdown-item-button" variant="secondary" className="round-button btn-icon menu-btn" title={<FontAwesomeIcon icon="bars" />}>
    <span >
      { items }
    </span>
  </DropdownButton>;

  const burgerMenu = <>
    <Navbar.Toggle aria-controls="offcanvasNavbar" className="navbar-toggle" onClick={menuClicked} />
    <Navbar.Offcanvas
      id="offcanvasNavbar"
      data-bs-theme={variant}
      aria-labelledby="offcanvasNavbarLabel"
      placement="end"
      show={showOffcanvas}
      onHide={handleOffcanvasClose}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title id="offcanvasNavbarLabel">{getDomainCfg().product.name}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
          { items }
      </Offcanvas.Body>
    </Navbar.Offcanvas>
  </>;

  const menuButton = props.burger ? burgerMenu : dropdownButton;

  return (
    <>
    <div className="form-inline">
      { props.alwaysShow ? menuButton : (((!features.showShareButton && !features.showAssetsButton && !features.cloudStorageAvailable) || menuButton)) }
    </div>
    </>
  );
}
